import { Injectable } from '@angular/core';
import { EntityState, EntityStore, StoreConfig } from '@datorama/akita';
import { TranslateService } from '@ngx-translate/core';
import { Observable, defer, of } from 'rxjs';
import { map, shareReplay, startWith } from 'rxjs/operators';

import { Article } from 'article/article';
import { ArticleI18n } from 'article/article-i18n';

import { AppLocator } from '../../app.locator';

export interface ArticleI18nDocument {
	readonly title: string;
	readonly locale: string;
	readonly content: string;
	readonly videoId: string;
	readonly pictureUrl: string;
}

export interface ArticleDocument {
	readonly id: string;
	readonly updateTimestamp: number;
	readonly localizations: ArticleI18nDocument[];
}

export type ArticleState = EntityState<ArticleDocument, string>;

@Injectable({ providedIn: 'root' })
@StoreConfig({ name: 'articles' })
export class ArticleStore extends EntityStore<ArticleState> {
	constructor() {
		super();
	}

	public get _translateService(): TranslateService {
		return AppLocator.inject(TranslateService);
	}

	public asDocument(article: Article): ArticleDocument {
		return article
			? {
					id: article.id,
					updateTimestamp: article.updateTimestamp,
					localizations: article.localizations.map((loc) => ({
						pictureUrl: loc.pictureUrl,
						videoId: loc.videoId,
						content: loc.content,
						locale: loc.locale,
						title: loc.title,
					})),
			  }
			: null;
	}

	public asEntity(document: ArticleDocument): Article {
		const localizations = !!document
			? document.localizations.map(
					(loc) =>
						new ArticleI18n(
							loc.title,
							loc.locale,
							loc.content,
							loc.videoId,
							loc.pictureUrl
						)
			  )
			: [];

		return document
			? new Article(
					document.id,
					document.updateTimestamp,
					localizations,
					this._observeLocalization(localizations)
			  )
			: null;
	}

	private _observeLocalization<T extends { locale: string }>(
		localizations: ReadonlyArray<T>
	): Observable<T> {
		return localizations && localizations.length > 0
			? defer(() =>
					this._translateService.onLangChange.pipe(
						map((e) => e.lang),
						startWith(this._translateService.currentLang)
					)
			  ).pipe(
					map((lang) => localizations.find((loc) => loc.locale === lang)),
					map((loc) => (!loc ? localizations[0] : loc)),
					shareReplay({ bufferSize: 1, refCount: true })
			  )
			: of(null);
	}
}
